// ════════════════════════════════════════════════════════════════════════
//  Umsortieren, Einschieben, Entfernen – ohne React
//
//  Die Anleitung nannte diese drei einmal bewusste Grenzen. Das Datenmodell
//  verlangt sie nicht: `order` ist ein Feld, und `checkKeysKept` prüft nur, dass
//  ein Schlüssel weiter EXISTIERT, nicht an welcher Stelle. Verboten ist
//  ausschließlich, einen veröffentlichten Schlüssel umzubenennen oder zu
//  entfernen – daran hängt der Fortschritt der Kinder.
//
//  Diese Funktionen fassen deshalb nie einen Schlüssel an. Sie ordnen Listen um
//  und führen `order` und `number` nach; das sind Anzeigefelder.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle, Lesson, Stage } from "@otto/lehrgang";
import { newStageKey } from "../../../shared/lib/contentKeys.js";

/**
 * `order` und `number` aus der Listenstelle neu bilden.
 *
 * Beide sind Anzeige: `order` die Reihenfolge in der Lern-App, `number` die
 * Plakette daneben. Nach jedem Umsortieren müssen sie lückenlos sein – ein Loch
 * in `order` wäre keine Fehlermeldung, sondern eine Lektion, die anderswo steht
 * als gedacht.
 */
function renumber(lessons: readonly Lesson[]): Lesson[] {
  return lessons.map((lesson, i) => ({ ...lesson, order: i, number: i + 1 }));
}

/** Ein Element um `delta` Stellen verschieben; außerhalb der Liste passiert nichts. */
function moved<T>(items: readonly T[], from: number, delta: number): T[] {
  const to = from + delta;
  if (from < 0 || to < 0 || to >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/**
 * Eine Lektion an eine Stelle setzen – das, was ein Zug in der Liste bedeutet.
 *
 * `toIndex` ist die Stelle in der Liste, wie man sie SIEHT: die gezogene Lektion
 * wird erst herausgenommen und dann dort eingesetzt. Ohne das Herausnehmen zuerst
 * verschöbe sich jedes Ziel unterhalb der Ausgangsstelle um eins.
 */
export function moveLessonTo(bundle: Bundle, lessonKey: string, toIndex: number): Bundle {
  const from = bundle.lessons.findIndex((l) => l.key === lessonKey);
  if (from === -1) return bundle;

  const rest = bundle.lessons.filter((_, i) => i !== from);
  const at = Math.max(0, Math.min(toIndex, rest.length));
  const lesson = bundle.lessons[from]!;
  return {
    ...bundle,
    lessons: renumber([...rest.slice(0, at), lesson, ...rest.slice(at)]),
  };
}

/** Eine Lektion um `delta` Stellen verschieben – die Tastaturfassung des Zugs. */
export function moveLesson(bundle: Bundle, lessonKey: string, delta: number): Bundle {
  const from = bundle.lessons.findIndex((l) => l.key === lessonKey);
  if (from === -1) return bundle;
  const to = from + delta;
  if (to < 0 || to >= bundle.lessons.length) return bundle;
  return moveLessonTo(bundle, lessonKey, to);
}

export function moveStage(
  bundle: Bundle,
  lessonKey: string,
  stageKey: string,
  delta: number,
): Bundle {
  return mapLesson(bundle, lessonKey, (lesson) => {
    const from = lesson.stages.findIndex((s) => s.key === stageKey);
    if (from === -1) return lesson;
    return { ...lesson, stages: moved(lesson.stages, from, delta) };
  });
}

/**
 * Eine Stufe an eine Stelle setzen – das, was ein Zug in der Liste bedeutet.
 *
 * Wie `moveLessonTo`: erst herausnehmen, dann einsetzen. Ohne das Herausnehmen
 * zuerst verschöbe sich jedes Ziel unterhalb der Ausgangsstelle um eins.
 *
 * Stufen tragen kein `order` – ihre Reihenfolge IST die Liste –, deshalb gibt es
 * hier nichts nachzuführen.
 */
export function moveStageTo(
  bundle: Bundle,
  lessonKey: string,
  stageKey: string,
  toIndex: number,
): Bundle {
  return mapLesson(bundle, lessonKey, (lesson) => {
    const from = lesson.stages.findIndex((s) => s.key === stageKey);
    if (from === -1) return lesson;
    const rest = lesson.stages.filter((_, i) => i !== from);
    const at = Math.max(0, Math.min(toIndex, rest.length));
    return {
      ...lesson,
      stages: [...rest.slice(0, at), lesson.stages[from]!, ...rest.slice(at)],
    };
  });
}

/**
 * Eine Lektion entfernen.
 *
 * Die Prüfung, ob sie schon veröffentlicht ist, steht NICHT hier, sondern an
 * der Oberfläche: dort ist bekannt, welche Schlüssel veröffentlicht sind, und
 * dort gehört die Begründung hin, warum der Knopf fehlt. Diese Funktion tut,
 * was man ihr sagt.
 */
export function removeLesson(bundle: Bundle, lessonKey: string): Bundle {
  return {
    ...bundle,
    lessons: renumber(bundle.lessons.filter((l) => l.key !== lessonKey)),
  };
}

export function removeStage(bundle: Bundle, lessonKey: string, stageKey: string): Bundle {
  return mapLesson(bundle, lessonKey, (lesson) => ({
    ...lesson,
    stages: lesson.stages.filter((s) => s.key !== stageKey),
  }));
}

/**
 * Eine entfernte Lektion aus der veröffentlichten Fassung zurückholen – mit
 * ihrem Schlüssel.
 *
 * Der Weg zurück aus einer Lage, die es seit der Löschregel gibt: entfernt
 * wurde, was ungeübt war, und danach hat ein Kind zum ersten Mal daran geübt.
 * Neu anlegen hilft dann nicht – der Assistent würfelt einen neuen Schlüssel,
 * und genau am alten hängt der Fortschritt.
 *
 * Eingesetzt wird an der Stelle, an der sie in der veröffentlichten Fassung
 * stand. `order` ist dort lückenlos; steht der Entwurf inzwischen kürzer da,
 * landet sie am Ende.
 */
export function restoreLesson(
  bundle: Bundle,
  published: Bundle,
  lessonKey: string,
): Bundle {
  // Schon da: nichts zu tun. Ein zweites Einsetzen machte zwei Lektionen mit
  // demselben Schlüssel, und das ist schlimmer als ein wirkungsloser Knopf.
  if (bundle.lessons.some((l) => l.key === lessonKey)) return bundle;

  const lesson = published.lessons.find((l) => l.key === lessonKey);
  if (!lesson) return bundle;

  const at = Math.max(0, Math.min(lesson.order, bundle.lessons.length));
  return {
    ...bundle,
    lessons: renumber([
      ...bundle.lessons.slice(0, at),
      lesson,
      ...bundle.lessons.slice(at),
    ]),
  };
}

/**
 * Dasselbe für eine einzelne Stufe einer Lektion, die es noch gibt.
 *
 * Die Stelle kommt aus der veröffentlichten Fassung. Stufen tragen kein
 * `order` – ihre Reihenfolge IST die Liste –, deshalb zählt hier der Index.
 */
export function restoreStage(
  bundle: Bundle,
  published: Bundle,
  lessonKey: string,
  stageKey: string,
): Bundle {
  const source = published.lessons.find((l) => l.key === lessonKey);
  const at = source?.stages.findIndex((s) => s.key === stageKey) ?? -1;
  if (!source || at === -1) return bundle;
  const stage = source.stages[at]!;

  return mapLesson(bundle, lessonKey, (lesson) => {
    if (lesson.stages.some((s) => s.key === stageKey)) return lesson;
    const stages = [...lesson.stages];
    stages.splice(Math.min(at, stages.length), 0, stage);
    return { ...lesson, stages };
  });
}

/**
 * Was in der veröffentlichten Fassung steht, im Entwurf fehlt und geübt ist.
 *
 * Nur diese drei zusammen ergeben einen Fall: was ungeübt entfernt wurde, ist
 * absichtlich weg und soll nicht als Rest herumliegen.
 */
export function removedButInUse(
  bundle: Bundle,
  published: Bundle | null,
  inUse: ReadonlySet<string>,
): { readonly lessons: readonly Lesson[]; readonly stages: readonly Stage[] } {
  if (!published) return { lessons: [], stages: [] };

  const draftLessons = new Set(bundle.lessons.map((l) => l.key));
  const lessons = published.lessons.filter(
    (l) => !draftLessons.has(l.key) && l.stages.some((s) => inUse.has(s.key)),
  );

  // Stufen nur aus Lektionen, die es im Entwurf noch GIBT: fehlt die ganze
  // Lektion, ist sie der Fall und nicht ihre einzelnen Stufen.
  const stages = published.lessons
    .filter((l) => draftLessons.has(l.key))
    .flatMap((l) => {
      const kept = new Set(
        bundle.lessons.find((d) => d.key === l.key)?.stages.map((s) => s.key) ?? [],
      );
      return l.stages.filter((s) => !kept.has(s.key) && inUse.has(s.key));
    });

  return { lessons, stages };
}

/**
 * Eine leere Stufe an einer Stelle einschieben.
 *
 * Der Schlüssel kommt aus `newStageKey` und ist ein Zufallswert – er sagt weder
 * etwas über die Stelle noch über die Lektion, in der die Stufe liegt. Beides
 * kann sich ändern, der Schlüssel nie.
 */
export function insertStage(
  bundle: Bundle,
  lessonKey: string,
  at: number,
  publishedKeys: ReadonlySet<string> = new Set(),
): Bundle {
  return mapLesson(bundle, lessonKey, (lesson) => {
    const stage: Stage = {
      key: newStageKey(bundle, publishedKeys),
      title: "Neue Stufe",
      hint: "Hinweis für die Kinder",
      text: starterText(lesson.keys),
    };
    const stages = [...lesson.stages];
    stages.splice(Math.max(0, Math.min(at, stages.length)), 0, stage);
    return { ...lesson, stages };
  });
}

function mapLesson(
  bundle: Bundle,
  lessonKey: string,
  change: (lesson: Lesson) => Lesson,
): Bundle {
  return {
    ...bundle,
    lessons: bundle.lessons.map((l) => (l.key === lessonKey ? change(l) : l)),
  };
}

/**
 * Startzeile aus dem Tastenvorrat der LEKTION.
 *
 * Nicht aus dem des Bundles: in einer Lektion mit engem Vorrat (4a kennt nur f
 * und j) wäre die neue Stufe sonst sofort rot.
 */
function starterText(keys: readonly string[]): string {
  const [a = "f", b = "j"] = keys.filter((k) => k !== " ");
  return `${a}${a}${a} ${b}${b}${b}`;
}
